import { theme, fonts } from "../theme";
import { Card, CardHeader, Pill } from "../components/ui";

const S = ({ children, title, icon, color }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: color || theme.blueLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: theme.text, letterSpacing: "-0.3px" }}>{title}</div>
    </div>
    {children}
  </div>
);

const Step = ({ n, title, desc }) => (
  <div style={{ display: "flex", gap: 14, padding: "14px 0", borderBottom: `1px solid ${theme.divider}` }}>
    <div style={{ width: 30, height: 30, borderRadius: 8, background: theme.blue, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, fontFamily: fonts.mono, flexShrink: 0 }}>{n}</div>
    <div><div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{title}</div><div style={{ fontSize: 12, color: theme.text3, lineHeight: 1.6 }}>{desc}</div></div>
  </div>
);

const Tip = ({ children, type = "info" }) => (
  <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 10, fontSize: 12, lineHeight: 1.6, background: type === "warn" ? theme.amberLight : type === "danger" ? theme.redLight : theme.blueLight, color: type === "warn" ? theme.amberText : type === "danger" ? theme.redText : theme.blueText, borderLeft: `3px solid ${type === "warn" ? theme.amber : type === "danger" ? theme.red : theme.blue}` }}>
    {children}
  </div>
);

const Key = ({ k }) => (
  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 5, background: theme.tint, border: `1px solid ${theme.border}`, fontFamily: fonts.mono, fontSize: 11, fontWeight: 700, color: theme.text2 }}>{k}</span>
);

export default function Guide() {
  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      {/* Hero */}
      <div className="sl-fadein" style={{ marginBottom: 32, padding: "28px 32px", borderRadius: 16, background: "linear-gradient(135deg, #EFF4FF, #F5F3FF)", border: `1px solid rgba(37,99,235,0.08)` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: theme.blueText, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 8 }}>Trading Platform Guide</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: theme.text, lineHeight: 1.3, marginBottom: 10 }}>Automatisierte SMC-Strategie: Backtest, Optimize, Deploy</div>
        <div style={{ fontSize: 13, color: theme.text2, lineHeight: 1.7 }}>
          Diese Plattform verbindet Pine Script SMC-Strategien mit einem automatisierten Python-Backtester, Optuna-Optimizer und Live-Execution via IBKR. Hier lernst du den kompletten Workflow.
        </div>
      </div>

      {/* Quick Start Workflow */}
      <S icon="🚀" title="Der vollständige Workflow: Strategie → Live" color={theme.greenLight}>
        <Card className="sl-fadein">
          <div style={{ padding: "4px 20px 16px" }}>
            <Step n="1" title="Strategie erstellen (Builder)" desc="Strategy Lab → Erstellen. Wähle Account-Typ (IBKR Paper/Live oder Topstep Combine/Express/Live), Instrument und Profil (Aggressiv/Standard/Konservativ). Bei Prop Firm werden Risk-Limits automatisch gesetzt (MLL, DLL, Max Contracts)." />
            <Step n="2" title="TF-Scanner (optional)" desc="Strategy Lab → TF finden. Testet alle Timeframe-Kombinationen und findet die beste Basis für dein Instrument." />
            <Step n="3" title="Backtest" desc="Strategy Lab → Testen. Erste Validierung: läuft die Strategie überhaupt profitabel auf dem Zeitraum?" />
            <Step n="4" title="Phase 1: Exploration (600 Trials)" desc="Strategy Lab → Optimieren → Exploration. Testet verschiedene Timeframes, Entry-Patterns, SL-Modi, TP-Strukturen. Alle Filter AUS für neutrales Ergebnis. Output: Beste Basis-Konfiguration. Apply Consensus klicken für statistisch robuste Werte." />
            <Step n="5" title="Phase 2: Feature Discovery (400 Trials)" desc="Testet mit fixierter Basis welche Features helfen (FVG, Regime, EMA, Trailing, BE, Loss Scaling). Output: Consensus welche Features AN/AUS. Confidence-Werte pro Feature." />
            <Step n="6" title="Phase 3: Parameter Tuning (250 Trials)" desc="Feintuning aller numerischen Parameter in eingeengten Ranges. Struktur fix, Features fix — nur noch Zahlen werden optimiert." />
            <Step n="7" title="Walk-Forward Validierung" desc="Rolling-Window Test mit 6 Monaten In-Sample + 3 Monaten Out-of-Sample. Zeigt ob die Strategie robust ist oder overfitted. ROBUST = ≥60% profitable Windows." />
            <Step n="8" title="Verifikation vs TradingView" desc="Verifikations-Panel öffnen (neben Go Live Button). 4 Steps: Parameter, TV-Setup, Checkliste, Live-Abgleich. Pine Script v3.7.5 mit exakt gleichen Parametern laden und Ergebnisse vergleichen." />
            <Step n="9" title="Go Live" desc="Wenn ROBUST + Verifikation OK: Go Live Button. Bot startet Live-Trading auf Paper-Port (4001) oder Live-Port (4002) über IBKR. Telegram-Benachrichtigungen aktiv." />
            <Step n="10" title="Portfolio Analyzer (bei mehreren Strategien)" desc="Portfolio → wähle deine Live-Strategien aus. Prüft Korrelationen, kombinierten DD, Margin-Auslastung, Concurrent Trades. Empfehlungen bei zu hoher Korrelation." />
          </div>
        </Card>
      </S>

      {/* Pages */}
      <S icon="💎" title="Prop Firm Integration (Topstep)" color={theme.purpleLight||"rgba(139,92,246,0.08)"}>
        <Card>
          <div style={{ padding: "14px 20px" }}>
            <div style={{ fontSize: 13, color: theme.text2, lineHeight: 1.7, marginBottom: 12 }}>
              Im Builder-Wizard Step 1 wählst du statt IBKR Paper/Live einen Topstep-Account. Die Risk-Limits werden dann als harte Constraints in Optimizer und Walk-Forward berücksichtigt.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 12 }}>
              {[
                ["Combine (Evaluation)", "Profit Target muss erreicht werden. $3K/$6K/$9K. Scaling Plan, Consistency Rule (max 50% Profit/Tag)."],
                ["Express Funded", "Payout-fähig. Kein Profit Target. Startbalance $0. Scaling Plan basierend auf Balance."],
                ["Live Funded", "Echtes Kapital. Dynamic Risk Expansion. Path to Reduction bei Drawdown."],
              ].map(([t, d]) => (
                <div key={t} style={{ padding: "12px 14px", borderRadius: 8, background: theme.tint }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{t}</div>
                  <div style={{ fontSize: 11, color: theme.text3, lineHeight: 1.5 }}>{d}</div>
                </div>
              ))}
            </div>
            <Tip type="warn">MLL (Maximum Loss Limit) ist End-of-Day Trailing — nicht intraday! Du kannst intraday tiefer sein und dich erholen. Am Tagesende zählt's.</Tip>
          </div>
        </Card>
      </S>

      <S icon="✨" title="Apply Consensus — Wisdom of Crowds" color={theme.blueLight}>
        <Card>
          <div style={{ padding: "14px 20px" }}>
            <div style={{ fontSize: 13, color: theme.text2, lineHeight: 1.7, marginBottom: 12 }}>
              Statt einen einzelnen Best-Trial zu übernehmen nutzt Apply Consensus die statistische Verteilung ALLER guten Trials (Top-25%):
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div style={{ padding: "12px 14px", borderRadius: 8, background: theme.greenLight }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: theme.greenText, marginBottom: 4 }}>✓ Kategorische Parameter</div>
                <div style={{ fontSize: 11, color: theme.text3, lineHeight: 1.5 }}>Wenn ≥80% der Top-Trials z.B. <code style={{fontFamily:fonts.mono}}>break_confirm=close</code> haben → wird fixiert.</div>
              </div>
              <div style={{ padding: "12px 14px", borderRadius: 8, background: theme.blueLight }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: theme.blueText, marginBottom: 4 }}>✓ Numerische Ranges</div>
                <div style={{ fontSize: 11, color: theme.text3, lineHeight: 1.5 }}>P10-P90 der Top-Trials → neue Optimizer-Range. Z.B. adx_threshold [10-30] → [18-25].</div>
              </div>
            </div>
            <Tip>Apply Consensus ist in allen 3 Phasen verfügbar und meist die beste Wahl. Einzelne Trial-Apply nur wenn du gezielt einen bestimmten Trial willst (z.B. niedrigster DD).</Tip>
          </div>
        </Card>
      </S>

      <S icon="📦" title="Portfolio Analyzer" color={theme.cyanLight||"rgba(6,182,212,0.08)"}>
        <Card>
          <div style={{ padding: "14px 20px" }}>
            <div style={{ fontSize: 13, color: theme.text2, lineHeight: 1.7, marginBottom: 12 }}>
              Bevor du mehrere Strategien parallel live schaltest: Portfolio Analyzer prüft ob sie zusammen harmonieren oder sich gegenseitig ruinieren.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                ["Korrelations-Matrix", "Pearson-Korrelation der täglichen P&L. Werte >0.7 = redundant (z.B. MNQ/MES/FDXS alle Indizes)."],
                ["Combined Drawdown", "Echter Portfolio-DD, nicht Summe der Einzel-DDs. Oft niedriger wenn Strategien unkorreliert."],
                ["Concurrent Trades", "Wie oft sind mehrere Strategien gleichzeitig im Trade? Max/Ø über die Historie."],
                ["Margin Peak", "Maximale benötigte Margin über die gesamte Historie. Prüft ob dein Account reicht."],
              ].map(([t, d]) => (
                <div key={t} style={{ padding: "12px 14px", borderRadius: 8, background: theme.tint }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{t}</div>
                  <div style={{ fontSize: 11, color: theme.text3, lineHeight: 1.5 }}>{d}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </S>

      {/* Pages */}
      <S icon="📊" title="Dashboard" color={theme.greenLight}>
        <div style={{ fontSize: 12.5, color: theme.text2, lineHeight: 1.7, marginBottom: 12 }}>
          Die Übersicht zeigt Portfolio-P&L, heute's Performance, Live-Bots mit Status, Equity-Kurve und die letzten Trades. Hover über die Equity-Kurve für exakte Werte.
        </div>
        <Tip>Alert-Banner erscheinen automatisch bei Position Mismatches zwischen Bot und IBKR oder wenn ein Live-Bot keinen Adapter-Connect hat.</Tip>
      </S>

      <S icon="🔬" title="Strategy Lab" color={theme.purpleLight}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          {[
            ["Backtest", "Strategie historisch testen. Parameter-Overrides möglich. Detail-View mit Equity, Drawdown, Monthly Heatmap, Trade-Liste (paginiert, sortierbar, CSV-Export)."],
            ["Optimizer", "Optuna-basierte Parameter-Suche. Ranges konfigurierbar. Trial-Convergence-Chart, Trial-Tabelle mit Grade (A-F). Best Params direkt als YAML speichern."],
            ["Walk-Forward", "Rolling-Window Validation. IS-Monate und OOS-Monate einstellbar. IS vs OOS Bar-Chart, Window-Tabelle, Robustness-Bewertung (ROBUST/FRAGILE)."],
            ["Scanner", "Testet alle TF-Kombinationen (Entry/Context/Regime). Ranking nach Composite Score mit Quality Dimensions. Klicke eine Zeile für Profitability/Risk/Consistency/Reliability."],
            ["Builder", "Strukturiertes Formular mit Instrument-Presets (Tick, PV, Margin auto-gesetzt). YAML-Preview live. Form ↔ YAML Toggle. Save/Clone/Delete/Promote."],
            ["AI Analyst", "Claude-powered Analyse. Fragt Strategie-Performance ab und gibt Optimierungsvorschläge."],
          ].map(([t, d]) => (
            <Card key={t} className="sl-card" style={{ padding: "14px 18px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 5 }}>{t}</div>
              <div style={{ fontSize: 11.5, color: theme.text3, lineHeight: 1.6 }}>{d}</div>
            </Card>
          ))}
        </div>
        <Tip type="warn">Backtest-Vergleich: Klicke den "vs" Button auf einer RunCard, dann "vs" auf einer zweiten → Side-by-Side Metrik-Vergleich mit Highlight des besseren Werts.</Tip>
      </S>

      <S icon="⚡" title="Bot Control" color={theme.amberLight}>
        <div style={{ fontSize: 12.5, color: theme.text2, lineHeight: 1.7, marginBottom: 12 }}>
          Start/Pause/Stop pro Strategy. Emergency Flatten schließt ALLE Positionen sofort bei IBKR (Confirmation-Dialog, kein Versehen möglich). System-Services zeigen Live-Status.
        </div>
        <Tip type="danger">Emergency Flatten ist unwiderruflich. Alle offenen Positionen werden market-closed. Nur im Notfall verwenden.</Tip>
      </S>

      <S icon="📓" title="Trade Journal" color={theme.cyanLight}>
        <div style={{ fontSize: 12.5, color: theme.text2, lineHeight: 1.7, marginBottom: 12 }}>
          Alle Trades mit Filter (All/Entries/Wins/Losses), sortierbaren Spalten (Time, Qty, Price, P&L) und Pagination (50 pro Seite). CSV-Export für externe Analyse in Excel.
        </div>
      </S>

      <S icon="🛡" title="Risk Dashboard" color={theme.redLight}>
        <div style={{ fontSize: 12.5, color: theme.text2, lineHeight: 1.7, marginBottom: 12 }}>
          Max Drawdown, Daily VaR (95%), Risk:Reward Ratio. Korrelationsmatrix berechnet echte Pearson-Korrelation aus täglichen P&L-Daten pro Instrument. Position-Sizing-Regeln als Referenz.
        </div>
        <Tip>Die Korrelation braucht mindestens 5 gemeinsame Handelstage pro Instrument-Paar für aussagekräftige Werte.</Tip>
      </S>

      <S icon="💾" title="Data Pipeline" color={theme.tint}>
        <div style={{ fontSize: 12.5, color: theme.text2, lineHeight: 1.7 }}>
          Übersicht der Strategy-Dateien, Webhook-Konfiguration (TradingView → Bot), konfigurierte Instrumente mit Status und Session-Zeiten, Telegram-Integration.
        </div>
      </S>

      {/* Key Concepts */}
      <S icon="🧠" title="Wichtige Konzepte" color={theme.purpleLight}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            ["SMC (Smart Money Concepts)", "Handelsansatz basierend auf institutionellem Orderflow: Change of Character (CHoCH), Break of Structure (BOS), Fair Value Gaps (FVG), Order Blocks."],
            ["Regime Filter", "ADX + ATR/SMA-basierter Marktregime-Detektor. Filtert Trades in Seitwärtsphasen aus. Modi: either (ADX oder ATR), both, adx_only, atr_only."],
            ["Walk-Forward Analysis", "Gold-Standard für Strategie-Validierung. In-Sample optimieren → Out-of-Sample testen → Fenster verschieben → wiederholen. ROBUST = ≥60% profitable Windows."],
            ["Composite Score", "Multi-dimensionale Bewertung: Profitability (PnL + PF + Expectancy), Risk Control (DD + Recovery), Consistency (Sharpe + WR/PF Balance), Reliability (Trade Count). Grade A-F."],
            ["Loss Scaling", "Automatische Positionsgrößen-Reduktion nach Verlustserien. Threshold 1: halbe Size nach 3 Losses. Threshold 2: Viertel-Size nach 6. Reset nach Recovery-Wins."],
            ["Partial Exit", "4-stufiger Take-Profit: z.B. 35% bei 3R, 30% bei 4.5R, 25% bei 6.5R, 10% bei 14R. Trailing aktiviert nach letztem Partial."],
          ].map(([t, d]) => (
            <div key={t} style={{ padding: "14px 16px", borderRadius: 10, background: theme.tint }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginBottom: 5 }}>{t}</div>
              <div style={{ fontSize: 11, color: theme.text3, lineHeight: 1.6 }}>{d}</div>
            </div>
          ))}
        </div>
      </S>

      {/* Keyboard Shortcuts */}
      <S icon="⌨️" title="Keyboard Shortcuts" color={theme.tint}>
        <Card>
          <div style={{ padding: "12px 20px" }}>
            {[
              ["1", "Dashboard"],
              ["2", "Strategy Lab"],
              ["3", "Bot Control"],
              ["4", "Trade Journal"],
              ["5", "Risk Dashboard"],
              ["6", "Data Pipeline"],
              ["7", "Guide"],
              ["8", "Portfolio Analyzer"],
              ["Esc", "Zurück zum Dashboard"],
            ].map(([k, d]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 14, padding: "8px 0", borderBottom: `1px solid ${theme.divider}` }}>
                <Key k={k} />
                <span style={{ fontSize: 12, color: theme.text2 }}>{d}</span>
              </div>
            ))}
          </div>
        </Card>
      </S>

      {/* Architecture */}
      <S icon="🏗" title="Architektur" color={theme.tint}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          {[
            ["Frontend", "Vite + React + Tailwind-Tokens", "Cloudflare Pages CI/CD via GitHub"],
            ["Backend API", "Python FastAPI auf Hetzner VPS", "CPX42, Ubuntu 24.04"],
            ["Execution", "IB Gateway headless via Xvfb", "Paper: DUH163514, Live: IBKR"],
            ["Backtester", "Custom Python Engine (Numba JIT)", "99%+ Match vs. Pine Script v3.7.5"],
            ["Optimizer", "Optuna TPE Sampler, n_jobs=8", "3-Phase Pipeline mit Consensus-Analyse"],
            ["Data", "16 Timeframes pre-computed in RAM", "M1/M5/M10/M15/M20/M25/M30/H1-H12/D"],
          ].map(([t, d1, d2]) => (
            <div key={t} style={{ padding: "14px 16px", borderRadius: 10, border: `1px solid ${theme.border}`, background: theme.white }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{t}</div>
              <div style={{ fontSize: 11, color: theme.text3, lineHeight: 1.5 }}>{d1}</div>
              <div style={{ fontSize: 10, color: theme.text4, marginTop: 3 }}>{d2}</div>
            </div>
          ))}
        </div>
      </S>

      {/* Version */}
      <div style={{ textAlign: "center", padding: "20px 0 40px", color: theme.text4, fontSize: 11 }}>
        Trading Platform v3.7.5 &middot; SMC MTF Strategy &middot; Built with React + FastAPI + Optuna + IBKR
      </div>
    </div>
  );
}
